/**
 * Builds the default MiniMax-H3 prompt for Chatter's video jobs.
 *
 * Chatter's video case is always the same one: a person from the reference
 * images, speaking the Chatter-generated audio, with perfect lipsync and no
 * other sound. That is narrow enough to write a correct prompt up front rather
 * than making the user discover the format.
 *
 * The format is MiniMax's own full-reference rewrite format, taken from their
 * guide (`~/code/promptsmith/guides/ref.md`, verbatim from the model repo):
 * six sections in a fixed order, with the task-type prefix on `summary` and
 * the fixed English relationship markers in `retention_analysis`.
 *
 * H3 Studio produces these with `promptsmith`, which runs a Claude session
 * against that guide. Chatter has no equivalent — the worker is in Modal's
 * cloud and promptsmith is on the LAN — so this fills the same role with a
 * template, at the cost of not adapting to an arbitrary creative brief.
 */

export interface H3Ref {
  /**
   * What this reference IS, in the user's words — "the man", "the kitchen",
   * "his leather jacket". Optional, and everything hinges on it: without a
   * label every image has to be assumed to be another view of one person,
   * which is what made extra references look ignored.
   */
  label?: string;
}

export interface H3PromptContext {
  /** One entry per reference image, in order. */
  refs?: H3Ref[];
  /** One entry per reference video, in order. */
  videoRefs?: H3Ref[];
  /**
   * The setting and action in the user's words. When given it replaces the
   * default talking-head framing rather than being appended to it — a
   * hardcoded "plain uncluttered background" sitting next to the user's own
   * scene is a contradiction, and the model follows the boilerplate.
   */
  scene?: string;
  refImageCount: number;
  refVideoCount: number;
  /** The text the parent TTS job spoke, if known. */
  text?: string;
  /** ISO 639-1 code from the parent job, or "auto". */
  language?: string;
  /**
   * True when only a window of a longer take is being animated. The spoken
   * words are then deliberately omitted: the transcript covers the whole take,
   * and telling the model to fit all of it into a 15s window would fight the
   * audio it is supposed to be matching.
   */
  windowed: boolean;
}

// `<d>` blocks carry a language name, not a code.
const LANGUAGE_NAMES: Record<string, string> = {
  en: "English", es: "Spanish", fr: "French", de: "German", it: "Italian",
  pt: "Portuguese", nl: "Dutch", pl: "Polish", ru: "Russian", ja: "Japanese",
  ko: "Korean", zh: "Chinese", ar: "Arabic", hi: "Hindi", tr: "Turkish",
  sv: "Swedish", da: "Danish", no: "Norwegian", fi: "Finnish", cs: "Czech",
  el: "Greek", he: "Hebrew", th: "Thai", vi: "Vietnamese", id: "Indonesian",
  uk: "Ukrainian", ro: "Romanian", hu: "Hungarian",
};

function languageName(code?: string): string {
  if (!code || code === "auto") return "English";
  return LANGUAGE_NAMES[code.toLowerCase().slice(0, 2)] ?? "English";
}

/**
 * MiniMax's guide: "preserve every original word and punctuation mark
 * verbatim", and standardise to basic written marks — no decorative or
 * repeated punctuation, no emoji.
 */
function cleanScript(text: string): string {
  return text
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/…/g, ".")
    .replace(/[~•·—–]+/g, " ")
    // Strip emoji and other pictographs.
    .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}]/gu, "")
    .replace(/([!?.,])\1+/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

export function buildDefaultPrompt(ctx: H3PromptContext): string {
  const { refImageCount, refVideoCount, text, language, windowed, scene } = ctx;

  const labelOf = (list: H3Ref[] | undefined, i: number) =>
    list?.[i]?.label?.trim() || "";

  const pictures = Array.from({ length: refImageCount }, (_, i) => `<Picture ${i + 1}>`);
  const videos = Array.from({ length: refVideoCount }, (_, i) => `<Video ${i + 1}>`);
  const assets = [...pictures, ...videos];
  const labels = [
    ...Array.from({ length: refImageCount }, (_, i) => labelOf(ctx.refs, i)),
    ...Array.from({ length: refVideoCount }, (_, i) => labelOf(ctx.videoRefs, i)),
  ];
  const anyLabelled = labels.some(Boolean);

  // With no labels, every asset is assumed to be another view of the speaker —
  // the original talking-head case. As soon as one is labelled, each asset gets
  // its own <Subject N> so the model has a reason to use more than the first.
  const speakerAssets = anyLabelled
    ? assets.filter((_, i) => !labels[i] || i === 0)
    : assets;
  const speakerList = (speakerAssets.length ? speakerAssets : [assets[0] ?? "<Picture 1>"]).join(", ");
  const plural = speakerAssets.length > 1;

  const extraSubjects = anyLabelled
    ? assets
        .map((asset, i) => ({ asset, label: labels[i], i }))
        .filter((a) => a.i > 0 && a.label)
        .map(
          (a, n) =>
            `<Subject ${n + 2}> is ${a.label}, shown in ${a.asset}. Reproduce it exactly as ${a.asset} shows it, with no change to its appearance, colour, materials, layout, or detail.`,
        )
    : [];

  const extraSubjectRetention = extraSubjects.map(
    (_, n) =>
      `<Subject ${n + 2}> (appears in [Shot 1]): fully_preserved - reproduced exactly as shown in its reference, and actually present and visible in the shot.`,
  );

  const script = text ? cleanScript(text) : "";
  const spoken = !windowed && script
    ? `\n<Subject 1> (S1) says: <d>[${languageName(language)}] ${script}</d>`
    : "";

  const excerptNote = windowed
    ? " <Audio 1> is a continuous excerpt of a longer recording, so <Subject 1> is already mid-speech on the first frame and is still mid-speech on the last; there is no pause or wind-down at either end."
    : "";

  const speakerLabel = labels[0] || "the person";
  const otherSubjectRefs = extraSubjects
    .map((_, n) => `<Subject ${n + 2}>`)
    .join(", ");

  // The user's scene replaces the default framing sentence; it never sits
  // alongside it.
  const sceneLine = scene?.trim()
    ? `${scene.trim()}${otherSubjectRefs ? ` ${otherSubjectRefs} ${extraSubjects.length > 1 ? "are" : "is"} visible in the shot as described.` : ""}`
    : "A medium close-up of <Subject 1> from the chest up, centred in frame, in soft even front lighting against a plain uncluttered background.";

  const gazeLine = scene?.trim()
    ? "<Subject 1> speaks."
    : "<Subject 1> looks directly into the lens and speaks.";

  const cameraLine = scene?.trim()
    ? ""
    : " The camera is locked off with no pan, tilt, zoom, or shake.";

  return `subject_definitions
<Subject 1> is ${speakerLabel} shown in ${speakerList}${plural ? ", who is the same individual in every one of them" : ""}. Reproduce their exact facial structure, bone structure, eye shape and eye colour, eyebrows, nose, mouth shape, jawline, skin tone and skin texture, any moles, freckles or scars, hairline, hair colour and hair style, facial hair, apparent age, and build. <Subject 1> is the only person who speaks in the target video.
${extraSubjects.length ? extraSubjects.join("\n") + "\n" : ""}<Audio 1> is the complete spoken audio of the target video and is the voice of <Subject 1> (S1).

summary
[reference generation + audio reuse] A single continuous shot in which <Subject 1> speaks the whole of <Audio 1>. ${speakerList} ${plural ? "supply" : "supplies"} <Subject 1>'s appearance only and ${plural ? "are" : "is"} not used as a frame of the target video.${extraSubjects.length ? ` ${otherSubjectRefs} ${extraSubjects.length > 1 ? "are" : "is"} taken from ${extraSubjects.length > 1 ? "their own references" : "its own reference"} and must actually appear in the shot.` : ""} <Audio 1> is reused unchanged as the target video's entire final audio track, and <Subject 1>'s mouth, jaw and throat movement match it exactly for its full duration.

retention_analysis
<Subject 1> (appears in [Shot 1]): fully_preserved - the face and identity from the reference ${plural ? "assets are" : "asset is"} reproduced exactly, with no change to features, proportions, apparent age, or skin detail, and no idealisation or beautification.
${extraSubjectRetention.length ? extraSubjectRetention.join("\n") + "\n" : ""}<Audio 1>: fully_copy - <Audio 1> is reused 1:1 as the target video's complete final audio track.

detailed_description
[Shot 1] One continuous take with no cuts, running the full length of the video. ${sceneLine} ${gazeLine} Their lip, jaw and cheek movement is precisely synchronised to every syllable of <Audio 1> from the first frame to the last, the mouth shapes match the consonants and vowels actually being spoken, and the mouth stays closed and still wherever <Audio 1> is silent.${excerptNote} <Subject 1> blinks naturally, holds relaxed eye contact, and makes only small head and shoulder movements and micro-expressions consistent with the delivery in <Audio 1>. The face stays unobstructed and in frame for the entire shot.${cameraLine} The scene fills the frame edge to edge and the background continues past all four edges, with no letterboxing, pillarboxing, or black bands. No other person, voice, or sound source appears at any point.${spoken}

overall_soundscape
The only audio is <Audio 1>. No room tone, ambience, footsteps, clothing rustle, handling noise, audience, or any other speech is added at any point.

non_diegetic_music
None. There is no background music, score, sting, or sound design anywhere in the video.`;
}

/**
 * True when a prompt is already a full-reference rewrite, so the worker should
 * ship it verbatim instead of appending its own scaffolding. Mirrors
 * `looks_like_full_rewrite()` in worker/h3_prompt.py — keep the two in step.
 */
export function isFullRewrite(prompt: string): boolean {
  return /^\s*subject_definitions\s*$/m.test(prompt);
}
